import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Flex,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { GithubOutlined, LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import { getTrendsPipelineStatus } from '../api';
import './workspaceInformation/wiPhase1.css';

const { Title, Text, Paragraph } = Typography;

function formatTimestamp(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function healthTagColor(level) {
  if (level === 'live') return 'success';
  if (level === 'degraded') return 'warning';
  return 'error';
}

function PagesTable({ title, rows, emptyLabel }) {
  const columns = useMemo(
    () => [
      {
        title: 'Name / slug',
        key: 'name',
        render: (_, row) => (
          <div>
            <Text strong>{row.name || row.slug || '—'}</Text>
            {row.slug && row.name && row.name !== row.slug ? (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {row.slug}
                </Text>
              </div>
            ) : null}
          </div>
        ),
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 120,
        render: (v) => v || '—',
      },
      {
        title: 'Trigger query',
        key: 'query',
        render: (_, row) => row.query || row.trigger || '—',
      },
      {
        title: 'Page',
        key: 'url',
        width: 100,
        render: (_, row) =>
          row.url ? (
            <Button type="link" href={row.url} target="_blank" rel="noopener noreferrer" icon={<LinkOutlined />} style={{ paddingInline: 0 }}>
              Open
            </Button>
          ) : (
            '—'
          ),
      },
    ],
    []
  );

  return (
    <Card title={title} style={{ marginTop: 16 }} size="small">
      {!rows || rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyLabel} />
      ) : (
        <Table
          size="small"
          pagination={false}
          rowKey={(row, idx) => `${row.slug || row.name || 'row'}-${idx}`}
          dataSource={rows}
          columns={columns}
          scroll={{ x: true }}
        />
      )}
    </Card>
  );
}

export default function TrendsPipelineStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getTrendsPipelineStatus();
      setData(res.data);
    } catch (err) {
      console.error('Trends pipeline status error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load trends pipeline status');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = data?.lastRun?.counts || {};
  const healthLevel = data?.health?.level || 'broken';

  return (
    <div className="wi-page">
      <div className="wi-panel" style={{ maxWidth: 1100 }}>
        <Card bordered={false} className="wi-search-card">
          <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
            <div>
              <Title level={3} style={{ margin: 0 }}>
                Trends pipeline status
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
                Live view of the social-profile trends automation on <Text code>main</Text>.
              </Paragraph>
            </div>
            <Space wrap>
              {data?.links?.workflowRuns ? (
                <Button
                  href={data.links.workflowRuns}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<GithubOutlined />}
                >
                  GitHub Actions runs
                </Button>
              ) : null}
              <Button type="primary" icon={<ReloadOutlined />} onClick={load} loading={loading}>
                Refresh
              </Button>
            </Space>
          </Flex>
        </Card>

        {loading && !data ? (
          <div style={{ marginTop: 48, textAlign: 'center' }}>
            <Spin size="large" tip="Loading pipeline status…" />
          </div>
        ) : null}

        {error ? (
          <Alert type="error" showIcon message={error} style={{ marginTop: 16 }} />
        ) : null}

        {data ? (
          <>
            <Card style={{ marginTop: 16 }} className="wi-details-card">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Flex align="center" gap={12} wrap="wrap">
                  <Tag color={healthTagColor(healthLevel)} style={{ fontSize: 14, padding: '4px 10px' }}>
                    {data.health?.label || healthLevel}
                  </Tag>
                  <Text>{data.health?.reason || '—'}</Text>
                </Flex>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Source: {data.source || 'run_log'}
                  {data.meta?.listingMethod ? ` · listing: ${data.meta.listingMethod}` : ''}
                  {data.meta?.generated_at ? ` · fetched ${formatTimestamp(data.meta.generated_at)}` : ''}
                </Text>
              </Space>
            </Card>

            <Card title="Last run" style={{ marginTop: 16 }} className="wi-details-card">
              {data.lastRun ? (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <Descriptions
                    bordered
                    size="small"
                    column={{ xs: 1, sm: 2, md: 4 }}
                  >
                    <Descriptions.Item label="Date">
                      <Text strong>{data.lastRun.date || '—'}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Started">
                      <Text strong>{formatTimestamp(data.lastRun.started_at)}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag>{data.lastRun.status || '—'}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="write_enabled">
                      <Tag color={data.lastRun.write_enabled ? 'success' : 'default'}>
                        {data.lastRun.write_enabled ? 'true' : 'false'}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>

                  <Row gutter={[16, 16]}>
                    <Col xs={12} sm={6}>
                      <Card size="small">
                        <Statistic title="Creates" value={counts.created ?? 0} />
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card size="small">
                        <Statistic title="Refreshes" value={counts.refreshed ?? 0} />
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card size="small">
                        <Statistic title="Rejected thin" value={counts.rejected_thin ?? 0} />
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card size="small">
                        <Statistic title="Errors" value={counts.errors ?? 0} valueStyle={{ color: (counts.errors ?? 0) > 0 ? '#ff4d4f' : undefined }} />
                      </Card>
                    </Col>
                  </Row>
                </Space>
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No run log available on main." />
              )}
            </Card>

            <PagesTable
              title="New pages"
              rows={data.created}
              emptyLabel="No newly created pages in the last run."
            />
            <PagesTable
              title="Updated pages"
              rows={data.refreshed}
              emptyLabel="No refreshed pages in the last run."
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
